import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Download } from 'lucide-react'
import Button from '@/components/button'
import FormField from '@/components/form-field'
import LookupSelect from '@/components/lookup-select'
import PageHeader from '@/components/page-header'
import RadioGroup from '@/components/radio-group'
import Select from '@/components/select'
import { useGerarRelatorioMutation } from '@/http/mutations/relatorios'
import { useClientesLookupQuery } from '@/http/queries/lookups'
import type { LookupItem } from '@/types/lookup'
import {
  TIPO_ARQUIVO_RELATORIO,
  TIPO_RELATORIO,
  tipoArquivoRelatorioOptions,
  tipoRelatorioOptions,
} from '@/types/relatorio'
import { useDebouncedValue } from '@/utility/use-debounced-value'
import styles from './style.module.css'

const MESES = [
  { value: '01', label: 'Janeiro' },
  { value: '02', label: 'Fevereiro' },
  { value: '03', label: 'Março' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Maio' },
  { value: '06', label: 'Junho' },
  { value: '07', label: 'Julho' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Setembro' },
  { value: '10', label: 'Outubro' },
  { value: '11', label: 'Novembro' },
  { value: '12', label: 'Dezembro' },
]

const ANOS_PARA_TRAS = 5

const construirOpcoesDeAno = (): { value: string; label: string }[] => {
  const anoAtual = new Date().getFullYear()
  return Array.from({ length: ANOS_PARA_TRAS }, (_, index) => {
    const ano = String(anoAtual - index)
    return { value: ano, label: ano }
  })
}

const schema = yup.object({
  clienteId: yup.string().required('Selecione o cliente'),
  relatorio: yup
    .string()
    .oneOf(Object.values(TIPO_RELATORIO))
    .required('Selecione o relatório'),
  mes: yup.string().required('Selecione o mês'),
  ano: yup.string().required('Selecione o ano'),
  tipoArquivo: yup
    .string()
    .oneOf(Object.values(TIPO_ARQUIVO_RELATORIO))
    .required('Selecione o tipo de arquivo'),
})

type RelatorioForm = yup.InferType<typeof schema>

const RelatoriosView = () => {
  const [termoCliente, setTermoCliente] = useState('')
  const [clienteEscolhido, setClienteEscolhido] = useState<LookupItem | null>(null)
  const termoClienteDebounced = useDebouncedValue(termoCliente)
  const clientes = useClientesLookupQuery(termoClienteDebounced, termoClienteDebounced.trim() !== '')
  const gerarRelatorio = useGerarRelatorioMutation()
  const anos = construirOpcoesDeAno()

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<RelatorioForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      clienteId: '',
      relatorio: TIPO_RELATORIO.Relatorio1,
      mes: '',
      ano: '',
      tipoArquivo: TIPO_ARQUIVO_RELATORIO.Excel,
    },
  })

  const onSubmit = (data: RelatorioForm) => {
    gerarRelatorio.mutate({
      clienteId: data.clienteId,
      filtro: {
        mes: data.mes,
        ano: data.ano,
        tipoArquivo: data.tipoArquivo,
        relatorio: data.relatorio,
        pageNumber: 1,
        pageSize: 9999,
      },
    })
  }

  return (
    <>
      <PageHeader title="Relatórios" subtitle="Gere relatórios operacionais por cliente e período" />

      <form className={styles.card} onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField label="Cliente" htmlFor="clienteId" required error={errors.clienteId?.message}>
          <Controller
            name="clienteId"
            control={control}
            render={({ field }) => (
              <LookupSelect
                id="clienteId"
                termo={termoCliente}
                onTermoChange={setTermoCliente}
                itens={clientes.data ?? []}
                isLoading={clientes.isFetching}
                selecionado={clienteEscolhido}
                placeholder="Preencha o nome ou o CNPJ"
                onSelecionar={(item) => {
                  setClienteEscolhido(item)
                  field.onChange(item?.id ?? '')
                }}
              />
            )}
          />
        </FormField>

        <FormField
          label="Relatório"
          htmlFor="relatorio"
          required
          error={errors.relatorio?.message}
        >
          <Select
            id="relatorio"
            options={tipoRelatorioOptions}
            invalid={Boolean(errors.relatorio)}
            {...register('relatorio')}
          />
        </FormField>

        <div className={styles.periodo}>
          <FormField label="Mês" htmlFor="mes" required error={errors.mes?.message}>
            <Select
              id="mes"
              options={MESES}
              invalid={Boolean(errors.mes)}
              {...register('mes')}
            />
          </FormField>

          <FormField label="Ano" htmlFor="ano" required error={errors.ano?.message}>
            <Select
              id="ano"
              options={anos}
              invalid={Boolean(errors.ano)}
              {...register('ano')}
            />
          </FormField>
        </div>

        <FormField label="Tipo de arquivo" required error={errors.tipoArquivo?.message}>
          <RadioGroup
            options={tipoArquivoRelatorioOptions}
            defaultValue={TIPO_ARQUIVO_RELATORIO.Excel}
            ariaLabel="Tipo de arquivo"
            {...register('tipoArquivo')}
          />
        </FormField>

        <div className={styles.actions}>
          <Button type="submit" iconLeft={<Download size={16} />} isLoading={gerarRelatorio.isPending}>
            Gerar relatório
          </Button>
        </div>
      </form>
    </>
  )
}

export default RelatoriosView
